import { AppDispatch } from '../../store/store';
import { fetchCompanies } from '../../store/thunks/companiesThunk';
import {
  AddressParts,
  Company,
  CompanyLocation,
  SettlementSuggestion,
  StreetSuggestion,
} from '../../types';
import axiosInstance from '../../utils/axiosInstance';
import style from './Company.module.css';
import { Plan } from './planTypes';

// Повна картка однієї компанії за GUID власника (== адмінський id компанії) — щоб
// відкрити картку компанії після переходу з картки дзвінка (Історія перекладів).
export const getCompanyById = async (companyId: string): Promise<Company> => {
  const response = await axiosInstance.get('/Admin/GetCompany', {
    params: { companyId },
  });
  return response.data as Company;
};

export const loadCompanies = (
  dispatch: AppDispatch,
  pageIndex: React.MutableRefObject<number>,
  orderDirection: React.MutableRefObject<string>,
  orderBy?: React.MutableRefObject<string>,
) => {
  dispatch(
    fetchCompanies(
      pageIndex.current,
      18,
      orderDirection.current,
      '',
      orderBy?.current ?? 'name',
    ),
  );
};

export const handleSortArrowClick = (
  setIsRotated: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setIsRotated((prev) => !prev);
};

export const getPlanClass = (plan: string) => {
  switch (plan) {
    case Plan.FOP:
      return style.consultation;
    case Plan.EventTranslation:
      return style.eventTranslation;
    case Plan.Subscription:
      return style.subscription;
    case Plan.LegalEntity:
      return style.extended;
    case Plan.Network:
      return style.basic;
    default:
      return '';
  }
};

export const handleConfirmDelete = async (
  id: number,
  loadCompanies: () => void,
  setShowConfirmModal: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  // §FB38: помилку НЕ ковтаємо — той, хто викликає, показує тост і вирішує, що далі.
  try {
    await axiosInstance.delete('/Admin/RemoveCompany', {
      params: { companyId: id },
    });
  } catch (error) {
    setShowConfirmModal(false);
    throw error;
  }
  loadCompanies();
  setShowConfirmModal(false);
};

// §FB38: без внутрішнього catch — інакше UI показує «успіх» навіть при падінні API.
export const resendCompanyPasswordAndQr = async (id: number) => {
  await axiosInstance.post('/Admin/ResendCompanyPasswordAndQr', {
    id: id.toString(),
  });
};

// Один запис історії надсилання листа на email (дата/час, тип, кому, хто відправив).
export interface WidgetEmailLog {
  sentAt: string;
  recipientEmail: string;
  sentBy?: string;
  // Тип листа: 'widget' (QR для віджету) або 'access' (доступ: пароль+QR / запрошення).
  kind?: string;
}

export interface CompanyWidget {
  companyId: number;
  companyName: string;
  // Чи створено постійний лінк. До створення — false (поле/QR порожні).
  exists: boolean;
  // Чи є в компанії відповідальна особа (передумова створення лінка).
  hasResponsiblePerson: boolean;
  widgetUrl?: string;
  qrPngBase64?: string;
  // Табличка/NFC: та сама постійна ланка у формі містка /g (…/g?CompanyId=…&CallToken=…)
  // + QR високої корекції (у центрі якого адмінка малює NFC-іконку).
  tabletUrl?: string;
  tabletQrPngBase64?: string;
  // Історія надсилань коду на email, найновіші перші.
  emailHistory?: WidgetEmailLog[];
}

// Стан віджета: незмінний лінк (+QR), якщо створено; інакше порожній стан.
export const getCompanyWidget = async (
  companyId: number,
): Promise<CompanyWidget> => {
  const response = await axiosInstance.get('/Admin/GetCompanyWidget', {
    params: { companyId },
  });
  return response.data as CompanyWidget;
};

// Уніфікована історія надсилання ВСІХ листів компанії (доступ + QR-віджет),
// найновіші перші — для панелі «Історія листів» у картці компанії.
export const getCompanyEmailHistory = async (
  companyId: number,
): Promise<WidgetEmailLog[]> => {
  const response = await axiosInstance.get('/Admin/GetCompanyEmailHistory', {
    params: { companyId },
  });
  return (response.data as WidgetEmailLog[]) ?? [];
};

// §FB40: реєстр гостьових викликів компанії (сканування таблички QR/NFC → дзвінок).
// Outcome рахує бекенд: ringing | active | finished | canceled | missed.
export interface CompanyGuestCall {
  id: number;
  createdAt: string;
  acceptedAt?: string | null;
  finishedAt?: string | null;
  guestName?: string | null;
  statusId: number;
  durationSeconds?: number | null;
  outcome: 'ringing' | 'active' | 'finished' | 'canceled' | 'missed';
}

export const getCompanyGuestCalls = async (
  companyId: number,
): Promise<CompanyGuestCall[]> => {
  const response = await axiosInstance.get('/Admin/GetCompanyGuestCalls', {
    params: { companyId },
  });
  return (response.data as CompanyGuestCall[]) ?? [];
};

// §FB41: журнал усіх дзвінків користувачів компанії (вкладка «Дзвінки»).
export interface CompanyUserCall {
  id: number;
  createdAt: string;
  acceptedAt?: string | null;
  finishedAt?: string | null;
  statusId: number;
  durationSeconds?: number | null;
  outcome: 'ringing' | 'active' | 'finished' | 'canceled' | 'missed';
  // З погляду компанії: вихідний (співробітник дзвонив) чи вхідний.
  direction: 'outgoing' | 'incoming' | 'internal';
  employeeId: string;
  employeeName: string;
  counterpartName: string;
  rating?: number | null;
  ratingComment?: string | null;
}

export interface CompanyUserCallsPage {
  totalCount: number;
  hasNextPage: boolean;
  items: CompanyUserCall[];
}

export const getCompanyUserCalls = async (
  companyId: number,
  pageIndex = 0,
  pageSize = 30,
): Promise<CompanyUserCallsPage> => {
  const response = await axiosInstance.get('/Admin/GetCompanyUserCalls', {
    params: { companyId, pageIndex, pageSize },
  });
  const data = response.data as CompanyUserCallsPage;
  return {
    totalCount: data?.totalCount ?? 0,
    hasNextPage: !!data?.hasNextPage,
    items: data?.items ?? [],
  };
};

// §FB42: журнал 3-сторонніх викликів компанії (вкладка «QR для віджету»):
// ініціатор + відповідальна особа + перекладач.
export interface CompanyThreeWayCall {
  id: number;
  createdAt: string;
  acceptedAt?: string | null;
  finishedAt?: string | null;
  statusId: number;
  durationSeconds?: number | null;
  outcome: 'ringing' | 'active' | 'finished' | 'canceled' | 'missed';
  requesterName: string;
  receiverName: string;
  thirdName: string;
}

export interface CompanyThreeWayCallsPage {
  totalCount: number;
  hasNextPage: boolean;
  items: CompanyThreeWayCall[];
}

export const getCompanyThreeWayCalls = async (
  companyId: number,
  pageIndex = 0,
  pageSize = 30,
): Promise<CompanyThreeWayCallsPage> => {
  const response = await axiosInstance.get('/Admin/GetCompanyThreeWayCalls', {
    params: { companyId, pageIndex, pageSize },
  });
  const data = response.data as CompanyThreeWayCallsPage;
  return {
    totalCount: data?.totalCount ?? 0,
    hasNextPage: !!data?.hasNextPage,
    items: data?.items ?? [],
  };
};

// §FB44: заявки користувачів компанії (вкладка «Заявки»).
export interface CompanyOrder {
  id: number;
  orderDate: string;
  createdAt: string;
  clientName: string;
  translatorName: string;
  comment: string;
  status:
    | 'created'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'archived'
    | 'expired';
}

export interface CompanyOrdersPage {
  totalCount: number;
  hasNextPage: boolean;
  items: CompanyOrder[];
}

export const getCompanyOrders = async (
  companyId: number,
  pageIndex = 0,
  pageSize = 30,
): Promise<CompanyOrdersPage> => {
  const response = await axiosInstance.get('/Admin/GetCompanyOrders', {
    params: { companyId, pageIndex, pageSize },
  });
  const data = response.data as CompanyOrdersPage;
  return {
    totalCount: data?.totalCount ?? 0,
    hasNextPage: !!data?.hasNextPage,
    items: data?.items ?? [],
  };
};

// «Створити посилання» — створює постійний незмінний лінк ОДИН раз
// (лише якщо в компанії є відповідальна особа; інакше бекенд відповість помилкою).
export const createCompanyWidget = async (
  companyId: number,
): Promise<CompanyWidget> => {
  const response = await axiosInstance.post('/Admin/CreateCompanyWidget', {
    companyId,
  });
  return response.data as CompanyWidget;
};

export interface SendWidgetEmailPayload {
  companyId: number;
  recipientEmail: string;
  recipientName?: string;
  recipientPosition?: string;
  contractNumber?: string;
  // Конкретне посилання (per-row «email» з історії); без нього — поточне основне.
  callToken?: string;
}

export interface SendWidgetEmailResult {
  status: string;
  outgoingNumber: string;
  recipient: string;
  subject: string;
}

// Надсилання widget-QR на email офіційним листом (вих.№, відповідальні особи, відмінки).
export const sendCompanyWidgetEmail = async (
  payload: SendWidgetEmailPayload,
): Promise<SendWidgetEmailResult> => {
  const response = await axiosInstance.post(
    '/Admin/SendCompanyWidgetEmail',
    payload,
  );
  return response.data as SendWidgetEmailResult;
};

/* ===== §FB31 «Локації» — адреси, де впроваджено переклад WordCraft ===== */

// Розбір суцільного рядка адреси на складові (бекенд: парсер + реєстрове звіряння).
export const parseAddress = async (address: string): Promise<AddressParts> => {
  const response = await axiosInstance.post('/Admin/ParseAddress', { address });
  return response.data as AddressParts;
};

export const getCompanyLocations = async (
  companyId: number,
): Promise<CompanyLocation[]> => {
  const response = await axiosInstance.get('/Admin/GetCompanyLocations', {
    params: { companyId },
  });
  return (response.data as CompanyLocation[]) ?? [];
};

// Зберігає локацію: суцільний рядок + складові (після перегляду/правок адміном).
export const addCompanyLocation = async (
  companyId: number,
  rawAddress: string,
  parts: AddressParts,
  npSettlementRef?: string | null,
  npStreetRef?: string | null,
): Promise<CompanyLocation> => {
  const response = await axiosInstance.post('/Admin/AddCompanyLocation', {
    companyId,
    rawAddress,
    parts,
    npSettlementRef: npSettlementRef || null,
    npStreetRef: npStreetRef || null,
  });
  return response.data as CompanyLocation;
};

// §FB32 typeahead: підказки населених пунктів із реєстру НП (мінімум 2 літери).
export const searchSettlements = async (
  query: string,
): Promise<SettlementSuggestion[]> => {
  const response = await axiosInstance.get('/Admin/SearchSettlements', {
    params: { query },
  });
  return (response.data as SettlementSuggestion[]) ?? [];
};

// §FB32 typeahead: підказки вулиць у межах обраного пункту (реєстр НП).
export const searchSettlementStreets = async (
  settlementRef: string,
  query: string,
): Promise<StreetSuggestion[]> => {
  const response = await axiosInstance.get('/Admin/SearchSettlementStreets', {
    params: { settlementRef, query },
  });
  return (response.data as StreetSuggestion[]) ?? [];
};

export const removeCompanyLocation = async (
  locationId: number,
): Promise<void> => {
  await axiosInstance.delete('/Admin/RemoveCompanyLocation', {
    params: { locationId },
  });
};

// §FB33 «оновити координати»: повторне геокодування збереженої локації.
export const regeocodeCompanyLocation = async (
  locationId: number,
): Promise<CompanyLocation> => {
  const response = await axiosInstance.post(
    '/Admin/RegeocodeCompanyLocation',
    null,
    { params: { locationId } },
  );
  return response.data as CompanyLocation;
};

// §FB35 «уточнити точку»: клік адміна по супутниковій міні-карті — точне місце будинку
// (для вулиць/сіл, де OSM ще не має адресних точок). Джерело зберігається як «manual».
export const setCompanyLocationPoint = async (
  locationId: number,
  latitude: number,
  longitude: number,
): Promise<CompanyLocation> => {
  const response = await axiosInstance.post('/Admin/SetCompanyLocationPoint', {
    locationId,
    latitude,
    longitude,
  });
  return response.data as CompanyLocation;
};

// §FB36 тумблер «юридична адреса»: одна на компанію (бекенд знімає прапор з інших),
// у списку локацій юридична завжди показується першою.
export const setCompanyLocationLegal = async (
  locationId: number,
  isLegal: boolean,
): Promise<CompanyLocation> => {
  const response = await axiosInstance.post('/Admin/SetCompanyLocationLegal', {
    locationId,
    isLegal,
  });
  return response.data as CompanyLocation;
};
